// ── React ──
import { useState, useEffect, useRef } from "react";

// ── Firebase ──
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, setDoc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { getMessaging, getToken } from "firebase/messaging";

// ── Estilos ──
import "./App.css";

// ── i18n ──
import { t } from "./i18n/translations";

// ── Utils ──
import { generateId } from "./utils/idUtils";
import { getSharedInfo, getJoinInfo } from "./utils/urlUtils";
import { currencySymbol } from "./utils/currencyUtils";

// ── Hooks ──
import { useExchangeRates } from "./hooks/useExchangeRates";
import { useBirthdays } from "./hooks/useBirthdays";
import { useSharedMode } from "./hooks/useSharedMode";
import { useWishlistLoader } from "./hooks/useWishlistLoader";
import { useFamily } from "./hooks/useFamily";

// ── Handlers ──
import { createAuthHandlers } from "./handlers/authHandlers";
import { createGiftHandlers } from "./handlers/giftHandlers";

// ── Componentes ──
import BottomNav from "./components/BottomNav";
import FamilyView from "./components/Family/FamilyView";
import ProfileMenu from "./components/Menu/ProfileMenu";
import Tutorial from "./components/Tutorial";
import WishlistModal from "./components/Wishlist/WishlistModal";

// ── Modales ──
import {
  NicknameModal,
  CoopPanelModal,
  CooperateModal,
  CopyGiftModal,
  SantaConfirmModal,
  SantaEnvelopeModal,
  InfoModal,
  TermsModal,
  SimpleWishlistModal,
} from "./components/Modals";
import EditGroupModal from "./components/Modals/EditGroupModal";
import EditProfileModal from "./components/Modals/EditProfileModal";

// ── Screens ──
import SharedModeScreen from "./screens/SharedModeScreen";
import { JoinWelcomeScreen, JoinAuthScreen } from "./screens/JoinScreens";
import GiftListScreen from "./screens/GiftListScreen.jsx";
import AddGiftScreen from "./screens/AddGiftScreen";
import { LoginScreen, RegisterScreen } from "./screens/AuthScreens";

import BudgetScreen from "./screens/BudgetScreen";
import { getBudgetTotals, getBudgetTotalsAll } from "./utils/budgetUtils";


function getDetectedLang() {
  const nav = (navigator.language || "en").toLowerCase();
  if (nav.startsWith("es")) return "es";
  if (nav.startsWith("fi")) return "fi";
  return "en";
}

function getCachedJson(key, fallback) {
  try {
    const c = localStorage.getItem(key);
    return c ? JSON.parse(c) : fallback;
  } catch (e) {
    return fallback;
  }
}

export default function App() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(() => getCachedJson("whislu_profile", null));
  const [loading, setLoading] = useState(true);
  const [screen, setScreen] = useState("family");
  const [activeTab, setActiveTab] = useState("wishlu");
  const [toast, setToast] = useState("");

  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);

  const [viewingMember, setViewingMember] = useState(null);
  const [viewingWishlist, setViewingWishlist] = useState(null);
  const [prevViewingMember, setPrevViewingMember] = useState(null);
  const [prevViewingWishlist, setPrevViewingWishlist] = useState(null);

  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showBudgetScreen, setShowBudgetScreen] = useState(false);
  const [editingPriceValue, setEditingPriceValue] = useState("");
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [showWishlistDropdown, setShowWishlistDropdown] = useState(false);
  const [myWishlists, setMyWishlists] = useState([]);
  const [showCoopPanel, setShowCoopPanel] = useState(false);

  const [showWishlistModal, setShowWishlistModal] = useState(false);
  const [wishlistChoice, setWishlistChoice] = useState("new");
  const [existingWishlists, setExistingWishlists] = useState([]);
  const [selectedExistingId, setSelectedExistingId] = useState(null);
  const [pendingJoinFamilyId, setPendingJoinFamilyId] = useState(null);
  const [pendingNewGroupId, setPendingNewGroupId] = useState(null);
  const [newWishlistName, setNewWishlistName] = useState("");
  const [newGroupName, setNewGroupName] = useState("");

const [editProfileForm, setEditProfileForm] = useState({ name: "", birthday: "", color: "#9b59b6", language: "en", currency: "USD", country: "", mercadopago: "", paypal: "", amazonWishlist: "" });

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState(() => {
    const l = getDetectedLang();
    return { name: "", email: "", password: "", birthday: "", color: "#9b59b6", language: l };
  });
  const [landingLang, setLandingLang] = useState(getDetectedLang);
  const [authError, setAuthError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [giftForm, setGiftForm] = useState({ name: "", link: "", description: "", price: "", currency: "USD", photoUrl: "", photoFile: null, editId: null });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [autoFilling, setAutoFilling] = useState(false);
  const [copyGiftModal, setCopyGiftModal] = useState(null);
  const [copyTargetWishlistId, setCopyTargetWishlistId] = useState(null);
  const [copyWishlists, setCopyWishlists] = useState([]);

  const [giftSortOrder, setGiftSortOrder] = useState("date");
  const [memberSortOrder, setMemberSortOrder] = useState("birthday");
  const [memberViewMode, setMemberViewMode] = useState("grid");
  const [visibleGiftsCount, setVisibleGiftsCount] = useState(20);

  const [sharedMode, setSharedMode] = useState(false);
  const [cameFromShared, setCameFromShared] = useState(false);
  const [sharedMember, setSharedMember] = useState(null);
  const [sharedWishlist, setSharedWishlist] = useState(null);
  const [sharedLang, setSharedLang] = useState("en");
  const [joinMode, setJoinMode] = useState(false);
  const [joinFamilyId, setJoinFamilyId] = useState(null);
  const [joinFamilyName, setJoinFamilyName] = useState("");
  const [joinScreen, setJoinScreen] = useState("welcome");

  const [budgetInput, setBudgetInput] = useState("");

  const [wishlistImagesLoading, setWishlistImagesLoading] = useState(false);

  const [pullRefreshing] = useState(false);
  const [cooperateModal, setCooperateModal] = useState(null);
  const [cooperateAmount, setCooperateAmount] = useState("");
  const [cooperatePayError, setCooperatePayError] = useState(false);
  const [coopPanelData, setCoopPanelData] = useState(null);
  const [confirmedCoops, setConfirmedCoops] = useState([]);
  const [cameFromProfilePicker, setCameFromProfilePicker] = useState(false);
  
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem("whislu_dark") === "1"; } catch (e) { return false; }
  });
  const [showSimpleWishlistModal, setShowSimpleWishlistModal] = useState(false);
  const [simpleWishlistName, setSimpleWishlistName] = useState("");

  const fileRef = useRef();

  const detectedLang = getDetectedLang();
  const lang = userProfile?.language || detectedLang;
  const tr = (key) => t(lang, key);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2800);
  };

/* eslint-disable no-unused-vars */
const {
  currentFamily, setCurrentFamily,
  userFamilies, setUserFamilies,
  members, setMembers,
  groupIcons, isLeader,
  showAddManaged, setShowAddManaged,
  showEditName, setShowEditName,
  showSantaConfirm, setShowSantaConfirm,
  showSantaEnvelope, setShowSantaEnvelope,
  editingManaged, setEditingManaged,
  editingNickname, setEditingNickname,
  nicknameValue, setNicknameValue,
  setManagedName,setManagedColor,
  santaBudget, setSantaBudget,
  santaRevealName,
  editNameValue, setEditNameValue,
  editNameOpenedFrom, setEditNameOpenedFrom,
  editingGroupIcon, setEditingGroupIcon,
  popupFamily, setPopupFamily,
  wishlistNames,
  getDisplayName,
  saveGroupNickname,
  deleteManagedMember,
  saveGroupIcon,
  activateSantaSecreto,
  deactivateSantaSecreto,
  openSantaEnvelope,
  loadWishlistNames,
  managedName,      
  managedColor,  
  handleAddManagedMember,             
  saveEditedManaged,   
} = useFamily({ user, userProfile, setUserProfile, lang, showToast, tr });

  


  const makeLongPress = (onLongPress, ms = 600) => {
    const timer = { current: null };
    const fired = { current: false };
    return {
      onTouchStart: (e) => {
        fired.current = false;
        if (e.currentTarget) e.currentTarget.style.userSelect = "none";
        timer.current = setTimeout(() => { fired.current = true; if (navigator.vibrate) navigator.vibrate(40); onLongPress(); }, ms);
      },
      onTouchEnd: (e) => {
        clearTimeout(timer.current);
        if (e.currentTarget) e.currentTarget.style.userSelect = "";
        if (fired.current) { e.preventDefault(); e.stopPropagation(); fired.current = false; }
      },
      onTouchMove: (e) => { clearTimeout(timer.current); fired.current = false; if (e.currentTarget) e.currentTarget.style.userSelect = ""; },
      onMouseDown: () => { fired.current = false; timer.current = setTimeout(() => { fired.current = true; onLongPress(); }, ms); },
      onMouseUp: () => clearTimeout(timer.current),
      onMouseLeave: () => { clearTimeout(timer.current); fired.current = false; },
    };
  };

  const compressImage = (file) => new Promise((resolve) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const maxSize = 1200;
      let w = img.width, h = img.height;
      if (w > maxSize || h > maxSize) {
        if (w > h) { h = Math.round((h * maxSize) / w); w = maxSize; }
        else { w = Math.round((w * maxSize) / h); h = maxSize; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => resolve(blob || file), "image/jpeg", 0.75);
    };
    img.onerror = () => resolve(file);
    img.src = url;
  });

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setGiftForm((f) => ({ ...f, photoUrl: previewUrl, photoFile: file }));
    compressImage(file).then((compressed) => setGiftForm((f) => ({ ...f, photoFile: compressed })));
  };

  const handlerCtx = {
    user, userProfile, lang, currentFamily,
    viewingMember, viewingWishlist, prevViewingMember, prevViewingWishlist,
    giftForm, members, loginForm, registerForm, landingLang, joinMode, joinFamilyId,
    setUser, setUserProfile, setScreen, setActiveTab, setCurrentFamily, setUserFamilies,
    setViewingMember, setViewingWishlist, setPrevViewingMember, setPrevViewingWishlist,
    setMembers, setJoinMode, setJoinScreen, setAuthError,
    setExistingWishlists, setWishlistChoice, setSelectedExistingId,
    setPendingJoinFamilyId, setShowWishlistModal, setLandingLang, setRegisterForm,
    setGiftForm, setUploading, setUploadProgress, setAutoFilling,
    setCopyGiftModal, copyGiftModal, setCoopPanelData, setShowCoopPanel,
    setCooperateModal, setCooperateAmount, setSharedWishlist,
    showToast, tr, copyTargetWishlistId, setSharedMode,
    setShowTutorial,
  };

  const { handleRegister, handleForgotPassword, handleLogin, handleLogout } = createAuthHandlers(handlerCtx);
  const { saveGift, editGift, deleteGift, handleAutoFill, copyGiftToMyList, confirmCopyGift,
  commitGift, togglePurchased, uncommitGift, cooperateGift, loadCoopPanel, deleteCoopEntry,
  markAsPurchased, markAsNotPurchased,
} = createGiftHandlers(handlerCtx);

  // ── Effects ──
  // Tipos de cambio: descarga y cachea según la moneda del usuario
  const { exchangeRates } = useExchangeRates(userProfile?.currency);

  // Cumpleaños: avisa por toast si alguien cumple hoy o en 7 días
  useBirthdays({ user, members, userProfile, lang, showToast });

  // Modo compartido: lee el hash de la URL y carga el perfil/wishlist compartidos
  useSharedMode({
    setSharedMode,
    setSharedLang,
    setSharedMember,
    setSharedWishlist,
    setLoading,
    setJoinMode,
    setJoinFamilyId,
    setJoinFamilyName,
    setJoinScreen,
    setViewingMember,
    setViewingWishlist,
    setScreen,
    setActiveTab,
  });

  // Cargador de wishlist propia: al entrar a la pestaña wishlu la muestra sola
  useWishlistLoader({
    user,
    userProfile,
    activeTab,
    viewingMember,
    viewingWishlist,
    screen,
    setViewingMember,
    setViewingWishlist,
    setScreen,
  });

   useEffect(() => {
  const unsub = onAuthStateChanged(auth, async (u) => {
    setUser(u);
    if (u) {
      const snap = await getDoc(doc(db, "users", u.uid));
      if (snap.exists()) {
        const profile = snap.data();
        setUserProfile(profile);
        try { localStorage.setItem("whislu_profile", JSON.stringify(profile)); } catch (e) {}
        
        const join = getJoinInfo();
        if (join?.familyId) {
          const alreadyIn = (profile.families || []).includes(join.familyId);
          const familySnaps = await Promise.all((profile.families || []).map((fid) => getDoc(doc(db, "families", fid))));
          const fams = familySnaps.filter((s) => s.exists()).map((s) => ({ id: s.id, ...s.data() }));
          setUserFamilies(fams);
          if (alreadyIn) {
            const targetFamily = fams.find((f) => f.id === join.familyId) || fams[0];
            setCurrentFamily(targetFamily); window.location.hash = ""; setJoinMode(false); setScreen("family"); setActiveTab("wishlu");
            showToast(profile.language === "es" ? "¡Ya eres miembro de este grupo!" : "You're already in this group!");
          } else {
            const ids = profile.wishlists || [];
            const wSnaps = await Promise.all(ids.map((id) => getDoc(doc(db, "wishlists", id))));
            const wls = wSnaps.filter((s) => s.exists()).map((s) => ({ id: s.id, ...s.data() }));
            setExistingWishlists(wls); setWishlistChoice(wls.length > 0 ? "existing" : "new");
            setSelectedExistingId(wls.length > 0 ? wls[0].id : null); setPendingJoinFamilyId(join.familyId);
            setJoinMode(false); setScreen("family"); setShowWishlistModal(true);
          }
          if (!getSharedInfo()) setLoading(false);
          return;
        }

        const shared = getSharedInfo();
        if (shared) {
          const userSnap2 = await getDoc(doc(db, "users", shared.uid));
          if (userSnap2.exists()) {
            const userData2 = userSnap2.data();
            const member = { id: shared.uid, ...userData2 };
            const targetWishlistId = shared.wishlistId && userData2.wishlists?.includes(shared.wishlistId)
              ? shared.wishlistId
              : userData2.wishlists?.[0];
            if (targetWishlistId) {
              const wSnap2 = await getDoc(doc(db, "wishlists", targetWishlistId));
              if (wSnap2.exists()) {
                setViewingMember(member);
                setViewingWishlist({ id: wSnap2.id, ...wSnap2.data() });
                setScreen("profile");
                setActiveTab("wishlu");
              }
            }
          }
          setSharedMode(false);
          window.history.replaceState(null, "", window.location.pathname); // ← CAMBIO AQUÍ
          setLoading(false);
          return;
        }

        if (profile.families?.length > 0) {
          const familySnaps = await Promise.all(profile.families.map((fid) => getDoc(doc(db, "families", fid))));
          const fams = familySnaps.filter((s) => s.exists()).map((s) => ({ id: s.id, ...s.data() }));
          setUserFamilies(fams);
          if (fams.length > 0) {
            const lastFamilyId = (() => { try { return localStorage.getItem("whislu_last_family_id"); } catch (e) { return null; } })();
            const targetFamily = (lastFamilyId && fams.find((f) => f.id === lastFamilyId)) || fams[0];
            setCurrentFamily(targetFamily);
            try { localStorage.setItem("whislu_family", JSON.stringify(targetFamily)); } catch (e) {}
          }
        }
      }
    }
    if (!getSharedInfo()) setLoading(false);
  });
  return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  useEffect(() => { if (darkMode) document.documentElement.classList.add("dark"); else document.documentElement.classList.remove("dark"); }, [darkMode]);


  useEffect(() => {
    if (!user) return;
    async function setupNotifications() {
      try {
        const messaging = getMessaging();
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          const token = await getToken(messaging, { vapidKey: "93htMXLtk-R9oYfuKPLTJju5_NZL0aOUHIXsk0DDZnA" });
          if (token) await updateDoc(doc(db, "users", user.uid), { fcmToken: token });
        }
      } catch (e) { console.warn("Notifications not available:", e); }
    }
    setupNotifications();
  }, [user]);

  useEffect(() => {
    window.history.pushState({ screen }, "");
    const handleBack = () => {
      if (showEditName) { setShowEditName(false); setEditingGroupIcon(""); if (editNameOpenedFrom !== "groups") setActiveTab("profile"); window.history.pushState({ screen }, ""); return; }
      const anyModalOpen = document.querySelector(".modal-overlay");
      if (anyModalOpen) { anyModalOpen.dispatchEvent(new MouseEvent("click", { bubbles: true })); window.history.pushState({ screen }, ""); return; }
      if (showBudgetScreen) { setShowBudgetScreen(false); setShowBudgetGeneralMode(false); window.history.pushState({ screen }, ""); return; }
      if (screen === "profile") { setScreen("family"); setViewingMember(null); setViewingWishlist(null); }
      else if (screen === "add") { setScreen("profile"); }
      else { setScreen("family"); setViewingMember(null); setViewingWishlist(null); }
    };
    window.addEventListener("popstate", handleBack);
    return () => window.removeEventListener("popstate", handleBack);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBudgetScreen, showEditName, screen, editNameOpenedFrom]);

  // ── Handlers ──
  const toggleDarkMode = () => {
    const next = !darkMode; setDarkMode(next);
    try { localStorage.setItem("whislu_dark", next ? "1" : "0"); } catch (e) {}
    if (next) document.documentElement.classList.add("dark"); else document.documentElement.classList.remove("dark");
  };

  const viewProfile = async (member) => {
    const wishlistId = member.familyWishlists ? member.familyWishlists[currentFamily?.id] : member.wishlistId;
    let wishlist = { gifts: [] };
    if (wishlistId) { const wSnap = await getDoc(doc(db, "wishlists", wishlistId)); if (wSnap.exists()) wishlist = { id: wSnap.id, ...wSnap.data() }; }
    let fullMember = member;
    if (member.id && !member.isManaged) {
      try { const uSnap = await getDoc(doc(db, "users", member.id)); if (uSnap.exists()) fullMember = { id: member.id, ...uSnap.data(), gifts: member.gifts, wishlistId: member.wishlistId }; } catch (e) {}
    }
    setViewingMember(fullMember); setViewingWishlist(wishlist); setScreen("profile");
    setTimeout(() => { const p = document.querySelector(".pantalla"); if (p) p.scrollTop = 0; }, 20);
    const gifts = wishlist.gifts || [];
    if (gifts.some((g) => g.photoUrl)) { setWishlistImagesLoading(true); setTimeout(() => setWishlistImagesLoading(false), 1500); }
    gifts.slice(0, 20).forEach((g) => { if (g.photoUrl) { const img = new window.Image(); img.src = g.photoUrl; } });
  };

const [showBudgetEdit, setShowBudgetEdit] = useState(false);
const [showBudgetGeneralMode, setShowBudgetGeneralMode] = useState(false);
const [showBudgetGroupDropdown, setShowBudgetGroupDropdown] = useState(false);
const [editingPriceEntry, setEditingPriceEntry] = useState(null);

  const confirmSimpleWishlist = async () => {
    const uid = auth.currentUser?.uid; if (!uid) return;
    const wName = simpleWishlistName.trim() || ((userProfile?.name || "My") + "'s list");
    const wishlistId = generateId();
    const { setDoc: sd } = await import("firebase/firestore");
    await sd(doc(db, "wishlists", wishlistId), { ownerId: uid, name: wName, gifts: [] });
    const { updateDoc: ud, arrayUnion: au } = await import("firebase/firestore");
    await ud(doc(db, "users", uid), { wishlists: au(wishlistId) });
    setUserProfile((p) => ({ ...p, wishlists: [...(p?.wishlists || []), wishlistId] }));
    setShowSimpleWishlistModal(false); setSimpleWishlistName("");
    showToast(lang === "es" ? "✅ Wishlu creada" : lang === "fi" ? "✅ Wishlu luotu" : "✅ Wishlu created");
  };

  const confirmWishlistChoice = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid || !pendingJoinFamilyId) { showToast(tr("errorSaving")); return; }
    const fid = pendingJoinFamilyId;
    let wishlistId;
    if (wishlistChoice === "new") {
      wishlistId = generateId();
      const wName = (newWishlistName || "").trim() || ((userProfile?.name || "My") + "'s list");
      await setDoc(doc(db, "wishlists", wishlistId), { ownerId: uid, name: wName, gifts: [] });
      setNewWishlistName("");
    } else {
      wishlistId = selectedExistingId || existingWishlists[0]?.id;
      if (!wishlistId) { wishlistId = generateId(); await setDoc(doc(db, "wishlists", wishlistId), { ownerId: uid, name: (userProfile?.name || "My") + "'s list", gifts: [] }); }
    }
    if (pendingNewGroupId && pendingNewGroupId === fid) {
      const gName = newGroupName.trim() || (lang === "es" ? "Mi grupo" : lang === "fi" ? "Ryhmäni" : "My group");
      await setDoc(doc(db, "families", fid), { name: gName, leaderId: uid, memberIds: [uid], createdAt: Date.now() });
      const newFamily = { id: fid, name: gName, leaderId: uid };
      setCurrentFamily(newFamily); setUserFamilies((fs) => [...fs, newFamily]); setPendingNewGroupId(null); setNewGroupName("");
    }
    await updateDoc(doc(db, "families", fid), { memberIds: arrayUnion(uid) });
    await updateDoc(doc(db, "users", uid), { families: arrayUnion(fid), [`familyWishlists.${fid}`]: wishlistId, wishlists: arrayUnion(wishlistId) });
    setUserProfile((p) => ({ ...p, families: [...(p?.families || []), fid], familyWishlists: { ...(p?.familyWishlists || {}), [fid]: wishlistId }, wishlists: [...(p?.wishlists || []), wishlistId] }));
    const fSnap = await getDoc(doc(db, "families", fid));
    const newFamily = { id: fid, ...fSnap.data() };
    setCurrentFamily(newFamily);
    setUserFamilies((fs) => fs.find((f) => f.id === fid) ? fs.map((f) => (f.id === fid ? newFamily : f)) : [...fs, newFamily]);
    setShowWishlistModal(false); setPendingJoinFamilyId(null); window.location.hash = ""; setJoinMode(false);
    showToast(tr("welcome") + ", " + (userProfile?.name || "") + "!"); setScreen("family");
  };

  const saveProfile = async () => {
    if (!editProfileForm.name.trim()) return;
    const uid = user.uid;
    try {
      const newCountry = editProfileForm.country || userProfile?.country || "";
      await updateDoc(doc(db, "users", uid), { name: editProfileForm.name.trim(), birthday: editProfileForm.birthday || "", color: editProfileForm.color, language: editProfileForm.language, currency: editProfileForm.currency || userProfile?.currency || "USD", mercadopago: editProfileForm.mercadopago || "", paypal: editProfileForm.paypal || "", country: newCountry, amazonWishlist: editProfileForm.amazonWishlist || "", amazonWishlistVisibility: editProfileForm.amazonWishlistVisibility || [],});
      if (newCountry) { const wishlistIds = userProfile?.wishlists || []; if (wishlistIds.length > 0) await Promise.all(wishlistIds.map((wid) => updateDoc(doc(db, "wishlists", wid), { ownerCountry: newCountry }))); }
      setUserProfile((p) => ({ ...p, name: editProfileForm.name.trim(), birthday: editProfileForm.birthday || "", color: editProfileForm.color, language: editProfileForm.language, currency: editProfileForm.currency || p?.currency || "USD", mercadopago: editProfileForm.mercadopago || "", paypal: editProfileForm.paypal || "", country: newCountry,amazonWishlist: editProfileForm.amazonWishlist || "",
  amazonWishlistVisibility: editProfileForm.amazonWishlistVisibility || [],}));
      setMembers((ms) => ms.map((m) => m.id === uid ? { ...m, name: editProfileForm.name.trim(), birthday: editProfileForm.birthday || "", color: editProfileForm.color, country: newCountry } : m));
      setShowEditProfile(false); showToast(tr("saveProfile") + " ✓");
    } catch (e) { console.error("saveProfile error", e); showToast(tr("errorSaving")); }
  };

  const saveBudget = async () => {
    if (!user || !currentFamily) return;
    const val = parseFloat(budgetInput) || 0; const budgetCurrency = userProfile?.currency || "USD"; const fid = currentFamily.id;
    try {
      await updateDoc(doc(db, "users", user.uid), { [`groupBudgets.${fid}`]: val, [`groupBudgetCurrencies.${fid}`]: budgetCurrency });
      setUserProfile((p) => ({ ...p, groupBudgets: { ...(p?.groupBudgets || {}), [fid]: val }, groupBudgetCurrencies: { ...(p?.groupBudgetCurrencies || {}), [fid]: budgetCurrency } }));
      setShowBudgetEdit(false); setShowBudgetScreen(true); showToast(tr("budgetSaved"));
    } catch (e) { console.error("saveBudget error", e); showToast(tr("errorSaving")); }
  };


  const toggleNotifications = async () => {
    const current = userProfile?.birthdayNotifications !== false;
    await updateDoc(doc(db, "users", user.uid), { birthdayNotifications: !current });
    setUserProfile((p) => ({ ...p, birthdayNotifications: !current }));
    showToast(!current ? tr("notificationsOn") : tr("notificationsOff"));
  };





  const isMyProfile = user && viewingMember && (user.uid === viewingMember.id || (viewingMember.isManaged && viewingMember.managedBy === user.uid));
  const myMember = members.find((m) => m.id === user?.uid);
  const confirmedKeys = userProfile?.confirmedCoopKeys || [];
  const coopCount = coopPanelData !== null
  ? coopPanelData.length
  : (myMember?.gifts || []).reduce((acc, g) => {
      return acc + (g.cooperations || []).filter((c) => {
        const key = (myMember.wishlistId || "") + "_" + (g.id || "") + "_" + (c.uid || c.name || "") + "_" + (c.at || "");
        return !confirmedKeys.includes(key);
      }).length;
    }, 0); 
const profileLink = viewingMember
  ? window.location.origin + "/#/" + viewingMember.id + "/" + lang + "/" + (viewingWishlist?.id || "")
  : "";
const inviteLink = currentFamily 
  ? window.location.origin + "/#/join/" + currentFamily.id + "/" + encodeURIComponent(currentFamily.name)
  : "";
  // ── Loading ──
  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", background: darkMode ? "#121212" : "#faf8f5", color: "#999", gap: 16 }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 42, color: darkMode ? "#e8e2d9" : "#1a1a1a", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ color: "#d4a853", animation: "whislu-spin 2s linear infinite", display: "inline-block" }}>🌙</span>
          Wishlu
        </div>
      </div>
    );
  }

  if (sharedMode) {
    return (
      <SharedModeScreen sharedLang={sharedLang} sharedMember={sharedMember} sharedWishlist={sharedWishlist} user={user} toast={toast} setSharedMode={setSharedMode} setCameFromShared={setCameFromShared} setScreen={setScreen} togglePurchased={togglePurchased} />
    );
  }

  if (joinMode && !user && joinScreen === "welcome") {
    return <JoinWelcomeScreen landingLang={landingLang} setLandingLang={setLandingLang} setRegisterForm={setRegisterForm} joinFamilyName={joinFamilyName} tr={tr} setJoinScreen={setJoinScreen} />;
  }

  if (joinMode && !user && (joinScreen === "login" || joinScreen === "register")) {
    return <JoinAuthScreen joinScreen={joinScreen} setJoinScreen={setJoinScreen} landingLang={landingLang} loginForm={loginForm} setLoginForm={setLoginForm} registerForm={registerForm} setRegisterForm={setRegisterForm} authError={authError} joinFamilyName={joinFamilyName} handleLogin={handleLogin} handleRegister={handleRegister} handleForgotPassword={handleForgotPassword} termsAccepted={termsAccepted} setTermsAccepted={setTermsAccepted} setShowTermsModal={setShowTermsModal} toast={toast} />;
  }

  return (
    <>
      {/* ── MODALS ── */}
      {showSimpleWishlistModal && (
        <SimpleWishlistModal lang={lang} darkMode={darkMode} simpleWishlistName={simpleWishlistName} setSimpleWishlistName={setSimpleWishlistName} setShowSimpleWishlistModal={setShowSimpleWishlistModal} confirmSimpleWishlist={confirmSimpleWishlist} />
      )}

      {/* ── TUTORIAL ── */}
      {showTutorial && (
  <Tutorial
    lang={lang}
    darkMode={darkMode}
    tutorialStep={tutorialStep}
    setTutorialStep={setTutorialStep}
    setShowTutorial={setShowTutorial}
    setActiveTab={setActiveTab}
    setScreen={setScreen}
    setCurrentFamily={setCurrentFamily}
    userFamilies={userFamilies}
  />
)}

{showAddManaged && (
  <div className="modal-overlay" onClick={function(e) { if (e.target === e.currentTarget) setShowAddManaged(false); }}>
    <div className="modal">
      <h3 style={{ fontFamily: "Playfair Display, serif", fontSize: 18, marginBottom: 8 }}>{tr("addManagedMember")}</h3>
      <p style={{ fontSize: 13, color: "#999", marginBottom: 18 }}>{tr("addManagedMemberDesc")}</p>
      <div className="campo">
        <label className="campo-label">{tr("managedMemberName")}</label>
        <input className="campo-input" placeholder={tr("managedMemberNamePlaceholder")} value={managedName} onChange={function(e) { setManagedName(e.target.value); }} autoFocus />
      </div>
      <div className="campo">
        <label className="campo-label">{tr("profileColor")}</label>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <input type="color" value={managedColor} onChange={function(e) { setManagedColor(e.target.value); }} style={{ width: 48, height: 48, border: "none", borderRadius: 10, cursor: "pointer" }} />
          <div className="miembro-avatar" style={{ background: managedColor, width: 48, height: 48, fontSize: 20, margin: 0 }}>{managedName ? managedName.charAt(0).toUpperCase() : "?"}</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button className="btn btn-dorado" style={{ flex: 1 }} onClick={handleAddManagedMember} disabled={!managedName.trim()}>{tr("addMember")}</button>
        <button className="btn btn-gris" onClick={function() { setShowAddManaged(false); }}>{tr("cancel")}</button>
      </div>
    </div>
  </div>
)}

      <WishlistModal showWishlistModal={showWishlistModal} setShowWishlistModal={setShowWishlistModal} userProfile={userProfile} existingWishlists={existingWishlists} wishlistChoice={wishlistChoice} setWishlistChoice={setWishlistChoice} setSelectedExistingId={setSelectedExistingId} confirmWishlistChoice={confirmWishlistChoice} setPendingJoinFamilyId={setPendingJoinFamilyId} setPendingNewGroupId={setPendingNewGroupId} setActiveTab={setActiveTab} activeTab={activeTab} lang={lang} newWishlistName={newWishlistName} setNewWishlistName={setNewWishlistName} newGroupName={newGroupName} setNewGroupName={setNewGroupName} pendingNewGroupId={pendingNewGroupId} />

      {showInfoModal && <InfoModal landingLang={landingLang} setShowInfoModal={setShowInfoModal} />}
      {showTermsModal && <TermsModal landingLang={landingLang} setShowTermsModal={setShowTermsModal} setTermsAccepted={setTermsAccepted} />}
      {editingNickname && <NicknameModal lang={lang} darkMode={darkMode} userProfile={userProfile} currentFamily={currentFamily} nicknameValue={nicknameValue} setNicknameValue={setNicknameValue} setEditingNickname={setEditingNickname} saveGroupNickname={saveGroupNickname} tr={tr} />}
      {showCoopPanel && <CoopPanelModal lang={lang} darkMode={darkMode} user={user} userProfile={userProfile} setUserProfile={setUserProfile} coopPanelData={coopPanelData} setCoopPanelData={setCoopPanelData} confirmedCoops={confirmedCoops} setConfirmedCoops={setConfirmedCoops} setShowCoopPanel={setShowCoopPanel} deleteCoopEntry={deleteCoopEntry} showToast={showToast} />}
      {cooperateModal && <CooperateModal lang={lang} darkMode={darkMode} user={user} userProfile={userProfile} cooperateModal={cooperateModal} setCooperateModal={setCooperateModal} cooperateAmount={cooperateAmount} setCooperateAmount={setCooperateAmount} cooperatePayError={cooperatePayError} setCooperatePayError={setCooperatePayError} cooperateGift={cooperateGift} showToast={showToast} />}
      {copyGiftModal && <CopyGiftModal lang={lang} darkMode={darkMode} copyGiftModal={copyGiftModal} setCopyGiftModal={setCopyGiftModal} copyWishlists={copyWishlists} setCopyWishlists={setCopyWishlists} copyTargetWishlistId={copyTargetWishlistId} setCopyTargetWishlistId={setCopyTargetWishlistId} confirmCopyGift={confirmCopyGift} tr={tr} />}
      {showSantaConfirm && <SantaConfirmModal lang={lang} darkMode={darkMode} santaBudget={santaBudget} setSantaBudget={setSantaBudget} setShowSantaConfirm={setShowSantaConfirm} activateSantaSecreto={activateSantaSecreto} tr={tr} />}
      {showSantaEnvelope && <SantaEnvelopeModal lang={lang} currentFamily={currentFamily} santaRevealName={santaRevealName} setShowSantaEnvelope={setShowSantaEnvelope} tr={tr} />}


{editingManaged && (
  <div className="modal-overlay" onClick={function(e) { if (e.target === e.currentTarget) setEditingManaged(null); }}>
    <div className="modal">
      <h3 style={{ fontFamily: "Playfair Display, serif", fontSize: 18, marginBottom: 18 }}>{tr("editManaged")}</h3>
      <div className="campo">
        <label className="campo-label">{tr("managedMemberName")}</label>
        <input className="campo-input" value={editingManaged.name} onChange={function(e) { setEditingManaged(function(m) { return { ...m, name: e.target.value }; }); }} autoFocus />
      </div>
      <div className="campo">
        <label className="campo-label">{tr("profileColor")}</label>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <input type="color" value={editingManaged.color} onChange={function(e) { setEditingManaged(function(m) { return { ...m, color: e.target.value }; }); }} style={{ width: 48, height: 48, border: "none", borderRadius: 10, cursor: "pointer" }} />
          <div className="miembro-avatar" style={{ background: editingManaged.color, width: 48, height: 48, fontSize: 20, margin: 0 }}>{editingManaged.name ? editingManaged.name.charAt(0).toUpperCase() : "?"}</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button className="btn btn-negro" style={{ flex: 1 }} onClick={saveEditedManaged}>{tr("save")}</button>
        <button className="btn btn-gris" onClick={function() { setEditingManaged(null); }}>{tr("cancel")}</button>
      </div>
    </div>
  </div>
)}

      {/* ── EDIT GROUP POPUP ── */}
      {showEditName && (
  <EditGroupModal
    // — usuario y perfil —
    user={user}
    userProfile={userProfile}
    setUserProfile={setUserProfile}
    lang={lang}
    darkMode={darkMode}
 
    // — familia y miembros —
    currentFamily={currentFamily}
    userFamilies={userFamilies}
    popupFamily={popupFamily}
    setCurrentFamily={setCurrentFamily}
    setUserFamilies={setUserFamilies}
    setMembers={setMembers}           // ← necesario para abandonarGrupo
 
    // — íconos —
    groupIcons={groupIcons}
 
    // — campos editables —
    editNameValue={editNameValue}
    setEditNameValue={setEditNameValue}
    editNameOpenedFrom={editNameOpenedFrom}
    editingGroupIcon={editingGroupIcon}
    setEditingGroupIcon={setEditingGroupIcon}
    budgetInput={budgetInput}
    setBudgetInput={setBudgetInput}
 
    // — wishlists —
    wishlistNames={wishlistNames}
    loadWishlistNames={loadWishlistNames}
 
    // — helpers —
    showToast={showToast}
    tr={tr}
    setShowEditName={setShowEditName}
    setActiveTab={setActiveTab}
    saveGroupIcon={saveGroupIcon}
  />
)}
{showBudgetScreen && (
  <BudgetScreen
    lang={lang}
    darkMode={darkMode}
    userProfile={userProfile}
    currentFamily={currentFamily}
    user={user}
       exchangeRates={exchangeRates}
    setUserProfile={setUserProfile}
    userFamilies={userFamilies}
    groupIcons={groupIcons}
    showBudgetGeneralMode={showBudgetGeneralMode}
    showBudgetGroupDropdown={showBudgetGroupDropdown}
    editingPriceEntry={editingPriceEntry}
    editingPriceValue={editingPriceValue}
    budgetInput={budgetInput}
    setShowBudgetScreen={setShowBudgetScreen}
    setShowBudgetGeneralMode={setShowBudgetGeneralMode}
    setShowBudgetGroupDropdown={setShowBudgetGroupDropdown}
    setShowBudgetEdit={setShowBudgetEdit}
    setCurrentFamily={setCurrentFamily}
    setBudgetInput={setBudgetInput}
    setEditingPriceEntry={setEditingPriceEntry}
    setEditingPriceValue={setEditingPriceValue}
    getBudgetTotals={getBudgetTotals}
    getBudgetTotalsAll={getBudgetTotalsAll}
    markAsPurchased={markAsPurchased}
    markAsNotPurchased={markAsNotPurchased}
    uncommitGift={uncommitGift}
    showToast={showToast}
     tr={tr}
  />
)}


      {/* EDIT PROFILE MODAL */}
      {showEditProfile && (
  <EditProfileModal
    // — usuario y perfil —
    user={user}
    userProfile={userProfile}
    setUserProfile={setUserProfile}
    lang={lang}
    darkMode={darkMode}
 
    // — formulario —
    editProfileForm={editProfileForm}
    setEditProfileForm={setEditProfileForm}
 
    // — navegación —
    activeTab={activeTab}
    setActiveTab={setActiveTab}
    setScreen={setScreen}
 
    // — visibilidad —
    setShowEditProfile={setShowEditProfile}
 
    // — reseteo de estado global al eliminar cuenta —
    setCurrentFamily={setCurrentFamily}
    setUserFamilies={setUserFamilies}
    setMembers={setMembers}
 
    // — guardar perfil (lógica en App.js) —
    saveProfile={saveProfile}
 
    // — helpers —
    showToast={showToast}
    tr={tr}

    
  />
)}


      {/* ── MAIN APP SHELL ── */}
      <div className="app-shell">
        <header className="header" style={{ position: "relative" }}>
          {!user && (
            <button onClick={function() { setShowInfoModal(true); }} style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", background: "none", border: "1px solid #555", borderRadius: "50%", width: 28, height: 28, color: "#d4a853", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, zIndex: 2 }}>?</button>
          )}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, minWidth: 0, flex: 1 }}>
              <div className="header-titulo" style={{ cursor: "pointer", position: "relative" }} 
              onClick={async function(e) {
                e.stopPropagation();
                if (screen === "profile" && isMyProfile && (userProfile?.wishlists || []).length > 1) {
                  if (!showWishlistDropdown) { const ids = userProfile?.wishlists || []; const snaps = await Promise.all(ids.map(function(id) { return getDoc(doc(db, "wishlists", id)); })); setMyWishlists(snaps.filter(function(s) { return s.exists(); }).map(function(s) { return { id: s.id, ...s.data() }; })); }
                  setShowWishlistDropdown(function(v) { return !v; }); setShowGroupDropdown(false); return;
                }
                if (activeTab === "groups" && userFamilies.length > 1) { setShowGroupDropdown(function(v) { return !v; }); setShowWishlistDropdown(false); }
              }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {screen === "profile" && viewingMember ? (isMyProfile ? (viewingWishlist?.name || "Wishlu") : (viewingMember?.name || "Wishlu")) : activeTab === "groups" ? (currentFamily ? currentFamily.name : "Wishlu") : activeTab === "profile" ? tr("profileTab") : "Wishlu"}
                </span>
                {((userFamilies.length > 1 && activeTab === "groups") || (screen === "profile" && isMyProfile && (userProfile?.wishlists || []).length > 1)) && <span style={{ fontSize: 12, color: "#888", flexShrink: 0 }}>▾</span>}
              </div>
          </div>

          {showGroupDropdown && userFamilies.length > 1 && (
            <>
              <div style={{ position: "fixed", inset: 0, zIndex: 190 }} onClick={function() { setShowGroupDropdown(false); }} />
              <div style={{ position: "fixed", top: 56, left: 0, background: darkMode ? "#1e1e1e" : "#fff", border: "1px solid " + (darkMode ? "#2e2e2e" : "#e8e2d9"), borderRadius: "0 0 16px 16px", boxShadow: "0 8px 30px rgba(0,0,0,0.18)", zIndex: 195, minWidth: 220, overflow: "hidden" }}>
                {userFamilies.map(function(f) {
                  const isActive = currentFamily && currentFamily.id === f.id;
                  return (
                    <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 18px", cursor: "pointer", background: isActive ? (darkMode ? "#2a2a2a" : "#fffdf5") : "transparent", borderBottom: "1px solid " + (darkMode ? "#2e2e2e" : "#f0ece6") }}
                      onClick={function() { setCurrentFamily(f); try { localStorage.setItem("whislu_last_family_id", f.id); } catch(e) {} setScreen("family"); setViewingMember(null); setViewingWishlist(null); if (activeTab !== "groups") setActiveTab("wishlu"); setShowGroupDropdown(false); }}>
                      <span style={{ fontSize: 22 }}>{groupIcons[f.id] || "🌙"}</span>
                      <span style={{ fontSize: 14, fontWeight: isActive ? 600 : 400, color: isActive ? "#d4a853" : (darkMode ? "#e8e2d9" : "#1a1a1a"), flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                      {isActive && <span style={{ fontSize: 14, color: "#d4a853" }}>✓</span>}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {showWishlistDropdown && isMyProfile && (
            <>
              <div style={{ position: "fixed", inset: 0, zIndex: 190 }} onClick={() => setShowWishlistDropdown(false)} />
              <div style={{ position: "fixed", top: 56, left: 0, background: darkMode ? "#1e1e1e" : "#fff", border: "1px solid " + (darkMode ? "#2e2e2e" : "#e8e2d9"), borderRadius: "0 0 16px 16px", boxShadow: "0 8px 30px rgba(0,0,0,0.18)", zIndex: 195, minWidth: 220, overflow: "hidden" }}>
                {myWishlists.map(function(wl) {
                  const isActive = viewingWishlist?.id === wl.id;
                  return (
                    <div key={wl.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 18px", cursor: "pointer", background: isActive ? (darkMode ? "#2a2a2a" : "#fffdf5") : "transparent", borderBottom: "1px solid " + (darkMode ? "#2e2e2e" : "#f0ece6") }}
                      onClick={async function() { const wSnap = await getDoc(doc(db, "wishlists", wl.id)); if (wSnap.exists()) { try { localStorage.setItem("whislu_last_wishlist", wl.id); } catch(e) {} setViewingWishlist({ id: wSnap.id, ...wSnap.data() }); } setShowWishlistDropdown(false); }}>
                      <span style={{ fontSize: 22 }}>🎁</span>
                      <span style={{ fontSize: 14, fontWeight: isActive ? 600 : 400, color: isActive ? "#d4a853" : (darkMode ? "#e8e2d9" : "#1a1a1a"), flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{wl.name}</span>
                      <span style={{ fontSize: 11, color: "#aaa" }}>{(wl.gifts || []).length} regalos</span>
                      {isActive && <span style={{ fontSize: 14, color: "#d4a853" }}>✓</span>}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {!user && (
            <div style={{ display: "flex", gap: 4 }}>
              <button style={{ background: landingLang === "en" ? "#d4a853" : "transparent", border: "1px solid " + (landingLang === "en" ? "#d4a853" : "#555"), borderRadius: 8, padding: "4px 8px", cursor: "pointer", fontSize: 18, lineHeight: 1 }} onClick={function() { setLandingLang("en"); setRegisterForm(function(f) { return { ...f, language: "en" }; }); }}>🇬🇧</button>
              <button style={{ background: landingLang === "es" ? "#d4a853" : "transparent", border: "1px solid " + (landingLang === "es" ? "#d4a853" : "#555"), borderRadius: 8, padding: "4px 8px", cursor: "pointer", fontSize: 18, lineHeight: 1 }} onClick={function() { setLandingLang("es"); setRegisterForm(function(f) { return { ...f, language: "es" }; }); }}>🇲🇽</button>
              <button style={{ background: landingLang === "fi" ? "#d4a853" : "transparent", border: "1px solid " + (landingLang === "fi" ? "#d4a853" : "#555"), borderRadius: 8, padding: "4px 8px", cursor: "pointer", fontSize: 18, lineHeight: 1 }} onClick={function() { setLandingLang("fi"); setRegisterForm(function(f) { return { ...f, language: "fi" }; }); }}>🇫🇮</button>
            </div>
          )}
        </header>

        <div className="pantalla"><div className="contenido">
          {pullRefreshing && <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "12px 0 4px", gap: 8, color: "#d4a853", fontSize: 13 }}><div style={{ fontSize: 20, animation: "whislu-spin 1s linear infinite" }}>🌙</div>{lang === "es" ? "Actualizando…" : lang === "fi" ? "Päivitetään…" : "Refreshing…"}</div>}

          {!user && screen === "family" && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "70vh", gap: 0 }}>
              <div style={{ fontSize: 52, marginBottom: 16, filter: "drop-shadow(0 2px 8px rgba(212,168,83,0.4))", color: "#d4a853" }}>✦</div>
              <h1 style={{ fontFamily: "Playfair Display, serif", fontSize: 52, fontWeight: 400, color: darkMode ? "#e8e2d9" : "#1a1a1a", marginBottom: 10, letterSpacing: -1 }}>Wishlu</h1>
              <p style={{ fontSize: 15, color: "#aaa", marginBottom: 48, textAlign: "center" }}>{landingLang === "es" ? "Organiza tus deseos, compártelos y nunca olvides que regalar" : landingLang === "fi" ? "Järjestä toiveistasi, jaa ne ja älä koskaan unohda mitä lahjoittaa" : "Organize your wishes, share them and never forget what to give"}</p>
              <div style={{ width: "100%", maxWidth: 380, display: "flex", flexDirection: "column", gap: 10, padding: "0 4px" }}>
                <button className="btn btn-dorado btn-full" style={{ fontSize: 16, padding: "16px", borderRadius: 14, marginTop: 0 }} onClick={function() { setScreen("register"); setAuthError(""); }}>{landingLang === "es" ? "Crear cuenta" : landingLang === "fi" ? "Luo tili" : "Create account"}</button>
                <div style={{ textAlign: "center", color: "#bbb", fontSize: 13 }}>—</div>
                <button className="btn btn-negro btn-full" style={{ fontSize: 16, padding: "16px", borderRadius: 14, marginTop: 0 }} onClick={function() { setScreen("login"); setAuthError(""); }}>{t(landingLang, "signIn")}</button>
              </div>
            </div>
          )}

          {/* ── GROUPS TAB ── */}
          {activeTab === "groups" && screen === "family" && user && !currentFamily && (
            <div>
              <h2 className="seccion-titulo">{tr("myGroups")}</h2>
              <p className="seccion-subtitulo" style={{ marginBottom: 16 }}>{lang === "es" ? "Selecciona un grupo para verlo · mantén presionado para editarlo" : lang === "fi" ? "Valitse ryhmä · paina pitkään muokataksesi" : "Tap a group to view it · long press to edit"}</p>
              {userFamilies.map(function(f, index) {
  const isActive = currentFamily && currentFamily.id === f.id;
  const memberCount = f.memberIds ? f.memberIds.length : 0;
  const sym = currencySymbol(userProfile?.currency || "USD");
  const rawBudget = parseFloat((userProfile?.groupBudgets || {})[f.id] || 0);
  return (
    <div key={f.id} id={index === 0 ? "tutorial-group-item" : undefined} style={{ background: isActive ? (darkMode ? "#252525" : "#faf8f5") : (darkMode ? "#1e1e1e" : "#fff"), border: "1px solid " + (isActive ? "#d4a853" : (darkMode ? "#2e2e2e" : "#e8e2d9")), borderRadius: 16, padding: "14px 16px", marginBottom: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}
                    onClick={function() { setCurrentFamily(f); try { localStorage.setItem("whislu_last_family_id", f.id); } catch(e) {} setScreen("family"); setViewingMember(null); setViewingWishlist(null); }}
                    {...makeLongPress(function() {
                      setPopupFamily(f); setEditNameValue(f.name); setEditingGroupIcon(""); setBudgetInput(String((userProfile?.groupBudgets || {})[f.id] || "")); setEditNameOpenedFrom("groups");
                      const wlIds = userProfile?.wishlists || [];
                      if (wlIds.length > 0) { loadWishlistNames(); }
                      setShowEditName(true);
                    })}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 28 }}>{groupIcons[f.id] || "🌙"}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: darkMode ? "#e8e2d9" : "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "center" }}>{f.name}</div>
                        <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>{memberCount} {lang === "es" ? "miembro" + (memberCount !== 1 ? "s" : "") : lang === "fi" ? "jäsen" + (memberCount !== 1 ? "tä" : "") : "member" + (memberCount !== 1 ? "s" : "")}{rawBudget > 0 && <span> · 💰 {sym}{rawBudget.toFixed(0)}</span>}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {userFamilies.length === 0 && <div style={{ textAlign: "center", color: "#aaa", fontSize: 14, marginTop: 40 }}><div style={{ fontSize: 48, marginBottom: 12 }}>👥</div><div>{tr("noMembers")}</div></div>}
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", marginTop: 8, background: darkMode ? "#1e1e1e" : "#fff", border: "1px dashed " + (darkMode ? "#2e2e2e" : "#d4a853"), borderRadius: 16, cursor: "pointer", color: "#d4a853", fontWeight: 600, fontSize: 15 }}
                onClick={async function() {
                  const familyId = generateId(); const allIds = [...new Set(userProfile?.wishlists || [])];
                  const wSnaps = await Promise.all(allIds.map(function(id) { return getDoc(doc(db, "wishlists", id)); }));
                  const wls = wSnaps.filter(function(s) { return s.exists(); }).map(function(s) { return { id: s.id, ...s.data() }; });
                  setExistingWishlists(wls); setWishlistChoice(wls.length > 0 ? "existing" : "new"); setSelectedExistingId(wls.length > 0 ? wls[0].id : null);
                  setPendingJoinFamilyId(familyId); setPendingNewGroupId(familyId); setShowWishlistModal(true);
                }}>
                <span style={{ fontSize: 22 }}>➕</span>{tr("createNewGroup")}
              </div>
            </div>
          )}

          {/* ── FAMILY / MEMBERS VIEW ── */}
          {activeTab !== "profile" && screen === "family" && (activeTab !== "groups" || currentFamily) && (
            <div>
              {user && currentFamily?.santaSecreto?.active && (function() {
                const assignments = currentFamily.santaSecreto.assignments || {};
                const myManagedIds = members.filter(function(m) { return m.isManaged && m.managedBy === user.uid; }).map(function(m) { return m.id; });
                const allMyIds = [user.uid, ...myManagedIds];
                const hasAny = allMyIds.some(function(id) { return assignments[id]; });
                return hasAny ? (
                  <div onClick={openSantaEnvelope} style={{ display: "flex", alignItems: "center", gap: 12, background: "linear-gradient(135deg, #c0392b, #e74c3c)", borderRadius: 14, padding: "14px 18px", marginBottom: 16, cursor: "pointer", boxShadow: "0 4px 16px rgba(192,57,43,0.3)" }}>
                    <span style={{ fontSize: 32 }}>🎁</span>
                    <div style={{ flex: 1 }}><div style={{ color: "#fff", fontWeight: 600, fontSize: 15, fontFamily: "Playfair Display, serif" }}>{tr("santaSecretTitle")}</div><div style={{ color: "rgba(255,255,255,0.85)", fontSize: 13, marginTop: 2 }}>{tr("santaSecretReveal")}</div></div>
                    <span style={{ fontSize: 24 }}>✉️</span>
                  </div>
                ) : null;
              })()}

              {currentFamily && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                  <div style={{ background: darkMode ? "#1e1e1e" : "#fff", border: "1px solid " + (darkMode ? "#2e2e2e" : "#e8e2d9"), borderRadius: 14, padding: "14px", display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
                    <div><div style={{ fontSize: 11, fontWeight: 600, color: "#d4a853", letterSpacing: "0.5px", marginBottom: 5 }}>🔗 {lang === "es" ? "Invitar" : lang === "fi" ? "Kutsu" : "Invite"}</div><div style={{ fontSize: 11, color: "#999", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>{inviteLink}</div></div>
                    <div style={{ fontSize: 11, color: "#d4a853", fontWeight: 600, textAlign: "left", cursor: "pointer" }} onClick={function() { navigator.clipboard?.writeText(inviteLink); showToast(tr("linkCopied")); }}>← {tr("copy")}</div>
                    <div style={{ fontSize: 10, color: "#aaa", marginTop: -6 }}>{currentFamily?.name}</div>
                  </div>
                  {user ? (function() {
                    const sym = currencySymbol(userProfile?.currency || "USD");
                    const rawBudget = parseFloat((userProfile?.groupBudgets || {})[currentFamily.id] || 0);
                    const t2 =    getBudgetTotals(currentFamily.id, userProfile, exchangeRates); const usedAmt = t2.committed + t2.spent;
                    const pct = rawBudget > 0 ? Math.min(100, (usedAmt / rawBudget) * 100) : 0;
                    const barColor = pct > 90 ? "#c0392b" : pct > 70 ? "#e67e22" : "#d4a853";
                    const amtColor = pct > 90 ? "#c0392b" : pct > 70 ? "#e67e22" : (darkMode ? "#e8e2d9" : "#1a1a1a");
                    return (
                      <div style={{ background: darkMode ? "#1e1e1e" : "#fff", border: "1px solid " + (darkMode ? "#2e2e2e" : "#e8e2d9"), borderRadius: 14, padding: "14px", display: "flex", flexDirection: "column", gap: 10, cursor: "pointer", minWidth: 0 }}
                        onClick={function() { setBudgetInput(String(rawBudget || "")); setShowBudgetScreen(true); setShowBudgetGeneralMode(false); }}>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "#d4a853", letterSpacing: "0.5px", marginBottom: 6 }}>💰 {lang === "es" ? "Mi presupuesto" : lang === "fi" ? "Budjettini" : "My budget"}</div>
                          {rawBudget > 0 ? (<><div style={{ fontSize: 17, fontWeight: 700, color: amtColor, lineHeight: 1 }}>{sym}{usedAmt.toFixed(0)}<span style={{ fontSize: 12, color: "#999", fontWeight: 400 }}>&nbsp;/ {sym}{rawBudget.toFixed(0)}</span></div><div style={{ marginTop: 8, height: 5, background: darkMode ? "#333" : "#f0ece6", borderRadius: 4, overflow: "hidden" }}><div style={{ height: "100%", width: pct + "%", background: barColor, borderRadius: 4, transition: "width 0.4s" }} /></div></>) : usedAmt > 0 ? (<div style={{ fontSize: 17, fontWeight: 700, color: darkMode ? "#e8e2d9" : "#1a1a1a", lineHeight: 1 }}>{sym}{usedAmt.toFixed(0)}<span style={{ fontSize: 12, color: "#bbb", fontWeight: 400 }}>&nbsp;/ {lang === "es" ? "sin límite" : lang === "fi" ? "ei rajaa" : "no limit"}</span></div>) : (<div style={{ fontSize: 12, color: "#bbb" }}>{lang === "es" ? "Sin límite" : lang === "fi" ? "Ei rajaa" : "No limit"}</div>)}
                        </div>
                        <div style={{ fontSize: 11, color: "#d4a853", fontWeight: 600, textAlign: "right" }}>{lang === "es" ? "Ver →" : lang === "fi" ? "Näytä →" : "View →"}</div>
                      </div>
                    );
                  })() : (
                    <div style={{ background: darkMode ? "#1e1e1e" : "#fff", border: "1px solid " + (darkMode ? "#2e2e2e" : "#e8e2d9"), borderRadius: 14, padding: "14px", opacity: 0.5, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#d4a853", letterSpacing: "0.5px", marginBottom: 5 }}>💰 {lang === "es" ? "Mi presupuesto" : lang === "fi" ? "Budjettini" : "My budget"}</div>
                      <div style={{ fontSize: 12, color: "#aaa" }}>{lang === "es" ? "Inicia sesión" : lang === "fi" ? "Kirjaudu" : "Sign in"}</div>
                    </div>
                  )}
                </div>
              )}

              {user && currentFamily && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: members.length > 0 ? 8 : 16 }}>
                  {members.length === 0 ? <div /> : <div><h2 className="seccion-titulo">{tr("giftWishlists")}</h2><p className="seccion-subtitulo">{tr("tapMember")}</p></div>}
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {isLeader && currentFamily && <button onClick={function() { if (currentFamily?.santaSecreto?.active) { deactivateSantaSecreto(); } else { setShowSantaConfirm(true); } }} style={{ width: 36, height: 36, borderRadius: "50%", background: currentFamily?.santaSecreto?.active ? "linear-gradient(135deg, #c0392b, #e74c3c)" : (darkMode ? "#2a2a2a" : "#f5f0e8"), border: "1px solid " + (currentFamily?.santaSecreto?.active ? "#c0392b" : "#e0dbd2"), color: currentFamily?.santaSecreto?.active ? "#fff" : "#888", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>🎅</button>}
<button
  id="tutorial-add-managed"
  onClick={function() { setShowAddManaged(true); setManagedName(""); setManagedColor("#e67e22"); }}
  style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg, #d4a853, #c49340)", border: "none", color: "#fff", fontSize: 22, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, boxShadow: "0 2px 8px rgba(212,168,83,0.4)" }}>
  +
</button>                  </div>
                </div>
              )}

              {members.length > 0 && (
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: 12, gap: 8 }}>
                  <select value={memberSortOrder} onChange={function(e) { setMemberSortOrder(e.target.value); }} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #e0dbd2", background: darkMode ? "#2a2a2a" : "#faf8f5", color: darkMode ? "#e8e2d9" : "#1a1a1a", fontSize: 12, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", outline: "none" }}>
                    <option value="name">{tr("memberSortName")}</option><option value="birthday">{tr("memberSortBirthday")}</option>
                  </select>
                  <select value={memberViewMode} onChange={function(e) { setMemberViewMode(e.target.value); }} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #e0dbd2", background: darkMode ? "#2a2a2a" : "#faf8f5", color: darkMode ? "#e8e2d9" : "#1a1a1a", fontSize: 12, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", outline: "none" }}>
                    <option value="list">{lang === "es" ? "Lista" : lang === "fi" ? "Lista" : "List"}</option><option value="grid">{lang === "es" ? "Tarjetas" : lang === "fi" ? "Kortit" : "Cards"}</option><option value="compact">{lang === "es" ? "Comprimido" : lang === "fi" ? "Tiivis" : "Compact"}</option>
                  </select>
                </div>
              )}

              {activeTab === "groups" && currentFamily && <button className="btn btn-gris btn-sm" style={{ marginBottom: 16 }} onClick={function() { setCurrentFamily(null); }}>{tr("back")}</button>}
              {!user && members.length > 0 && <div style={{ marginBottom: 16 }}><h2 className="seccion-titulo">{tr("giftWishlists")}</h2><p className="seccion-subtitulo">{tr("tapMember")}</p></div>}

              {members.length > 0 && (
                <FamilyView members={members} memberViewMode={memberViewMode} memberSortOrder={memberSortOrder} user={user} currentFamily={currentFamily} userProfile={userProfile} lang={lang} darkMode={darkMode} viewProfile={viewProfile} setActiveTab={setActiveTab} setCameFromProfilePicker={setCameFromProfilePicker} setNicknameValue={setNicknameValue} setEditingNickname={setEditingNickname} setMembers={setMembers} deleteManagedMember={deleteManagedMember} getDisplayName={getDisplayName} makeLongPress={makeLongPress} showToast={showToast} tr={tr} />
              )}

              {!user && !joinMode && (
                <div className="landing">
                  <div className="landing-star">✦</div>
                  <div className="landing-title">Wishlu</div>
                  <div className="landing-sub">{landingLang === "es" ? "Organiza tus deseos, compártelos y nunca olvides que regalar" : landingLang === "fi" ? "Järjestä toiveistasi, jaa ne ja älä koskaan unohda mitä lahjoittaa" : "Organize your wishes, share them and never forget what to give"}</div>
                  <div className="landing-btns">
                    <button className="btn btn-dorado" onClick={function() { setScreen("register"); }}>{t(landingLang, "signUp")}</button>
                    <div style={{ fontSize: 13, color: "#ccc" }}>—</div>
                    <button className="btn btn-negro" onClick={function() { setScreen("login"); }}>{t(landingLang, "signIn")}</button>
                  </div>
                </div>
              )}
            </div>
          )}


          {activeTab === "profile" && screen === "family" && user && (
  <ProfileMenu
    user={user}
    userProfile={userProfile}
    setUserProfile={setUserProfile}
    lang={lang}
    darkMode={darkMode}
    members={members}
    userFamilies={userFamilies}
    coopPanelData={coopPanelData}
    showToast={showToast}
    setScreen={setScreen}
    setActiveTab={setActiveTab}
    setViewingMember={setViewingMember}
    setViewingWishlist={setViewingWishlist}
    setCameFromProfilePicker={setCameFromProfilePicker}
    setShowBudgetScreen={setShowBudgetScreen}
    setShowTutorial={setShowTutorial}
    setTutorialStep={setTutorialStep}
    setShowEditProfile={setShowEditProfile}
    setEditProfileForm={setEditProfileForm}
    toggleDarkMode={toggleDarkMode}
    toggleNotifications={toggleNotifications}
    handleLogout={handleLogout}
    loadCoopPanel={loadCoopPanel}
  />
)}

          {/* ── GIFT LIST SCREEN ── */}
          {screen === "profile" && activeTab !== "profile" && viewingMember && viewingWishlist && (
            <GiftListScreen lang={lang} darkMode={darkMode} exchangeRates={exchangeRates} user={user} userProfile={userProfile} viewingMember={viewingMember} viewingWishlist={viewingWishlist} setViewingWishlist={setViewingWishlist} currentFamily={currentFamily} isMyProfile={isMyProfile} showShareMenu={showShareMenu} setShowShareMenu={setShowShareMenu} wishlistImagesLoading={wishlistImagesLoading} profileLink={profileLink} giftSortOrder={giftSortOrder} setGiftSortOrder={setGiftSortOrder} visibleGiftsCount={visibleGiftsCount} setVisibleGiftsCount={setVisibleGiftsCount} cameFromProfilePicker={cameFromProfilePicker} setCameFromProfilePicker={setCameFromProfilePicker} setScreen={setScreen} setActiveTab={setActiveTab} setViewingMember={setViewingMember} setGiftForm={setGiftForm} setEditingManaged={setEditingManaged} setCooperateModal={setCooperateModal} setCooperateAmount={setCooperateAmount} tr={tr} makeLongPress={makeLongPress} deleteGift={deleteGift} editGift={editGift} commitGift={commitGift} uncommitGift={uncommitGift} copyGiftToMyList={copyGiftToMyList} deleteManagedMember={deleteManagedMember} showToast={showToast} useRealName={isMyProfile && !cameFromProfilePicker} budgetData={getBudgetTotals(currentFamily?.id, userProfile, exchangeRates)} userCurrency={userProfile?.currency || "USD"} />
          )}

          {/* ── ADD GIFT SCREEN ── */}
          {screen === "add" && (
  <AddGiftScreen lang={lang} darkMode={darkMode} user={user} userProfile={userProfile} giftForm={giftForm} setGiftForm={setGiftForm} uploading={uploading} uploadProgress={uploadProgress} autoFilling={autoFilling} prevViewingMember={prevViewingMember} prevViewingWishlist={prevViewingWishlist} viewingMember={viewingMember} setScreen={setScreen} setActiveTab={setActiveTab} setViewingMember={setViewingMember} setViewingWishlist={setViewingWishlist} setPrevViewingMember={setPrevViewingMember} setPrevViewingWishlist={setPrevViewingWishlist} saveGift={saveGift} handleAutoFill={handleAutoFill} handlePhoto={handlePhoto} fileRef={fileRef} deleteGift={deleteGift} />
)}

          {/* ── LOGIN ── */}
          {screen === "login" && !joinMode && (
  <LoginScreen
    landingLang={landingLang}
    loginForm={loginForm}
    setLoginForm={setLoginForm}
    authError={authError}
    cameFromShared={cameFromShared}
    handleLogin={handleLogin}
    handleForgotPassword={handleForgotPassword}
    setSharedMode={setSharedMode}
    setCameFromShared={setCameFromShared}
    setAuthError={setAuthError}
    setScreen={setScreen}
  />
)}


          {screen === "register" && !joinMode && (
  <RegisterScreen
    landingLang={landingLang}
    registerForm={registerForm}
    setRegisterForm={setRegisterForm}
    authError={authError}
    termsAccepted={termsAccepted}
    setTermsAccepted={setTermsAccepted}
    cameFromShared={cameFromShared}
    handleRegister={handleRegister}
    setSharedMode={setSharedMode}
    setCameFromShared={setCameFromShared}
    setAuthError={setAuthError}
    setScreen={setScreen}
    setShowTermsModal={setShowTermsModal}
  />
)}

        </div></div>
      </div>

      <BottomNav user={user} sharedMode={sharedMode} joinMode={joinMode} 
      activeTab={activeTab} screen={screen} lang={lang} members={members} 
      userProfile={userProfile} setActiveTab={setActiveTab} setScreen={setScreen} 
      setViewingMember={setViewingMember} setViewingWishlist={setViewingWishlist} 
      setCurrentFamily={setCurrentFamily} viewingWishlist={viewingWishlist}
      viewProfile={viewProfile} cameFromProfilePicker={cameFromProfilePicker} 
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }} 
      currentFamily={currentFamily} viewingMember={viewingMember} coopCount={coopCount} />

      {toast && <div className="toast">{toast}</div>}
    </>
  );
}
